import { useEffect, useState } from "react";
import { hasRole, useApi, useApp } from "../../context/AppContext";
import { ago } from "../../lib/fleet";
import { nameOf, useProductNames } from "../../lib/products";

// Layout & stock (docs/tasks/35, phase 4): the cabinet's planogram as the
// shelves see it, each product's training status on this cabinet, the stock
// count, and Update shelves. Editing the layout, refills and stocktakes are
// done in the capture app with the cabinet in front of you.

const STATUS_CHIP = {
  trained: "bg-emerald-100 text-emerald-800",
  partial: "bg-amber-100 text-amber-800",
  untrained: "bg-rose-100 text-rose-800",
};

function UpdateShelves({ deviceId, planogram, untrained, update, next, onStarted }) {
  const api = useApi();
  const { user } = useApp();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const may = hasRole(user, "tenant_admin", "ml_operator", "device_operator");
  const products = planogram?.product_uuids || [];
  const start = async () => {
    const extra = untrained.length ? `\n\n${untrained.length} product(s) on the layout are not trained on this cabinet's type; the update will not teach them.` : "";
    if (!window.confirm(`Build this cabinet's gallery from the ${products.length} products on its layout, package it and send it to the cabinet?\nThe cabinet switches to it between visits.${extra}`)) return;
    setBusy(true); setErr(null);
    try {
      await api.startShelfUpdate(deviceId, products, "admin panel");
      onStarted();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };
  const running = update && !["completed", "failed"].includes(update.status);
  return (
    <div className="card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Update shelves</h2>
          <p className="text-xs text-slate-500">One background job on the server per cabinet: build the gallery of the layout's products with the type's training rows, package it, send it.</p>
        </div>
        {may && <button className="btn btn-primary" disabled={busy || !products.length} onClick={start}>{busy ? "Starting…" : running ? "Queue another update" : "Update shelves"}</button>}
      </div>
      <dl className="mt-2 grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
        <dt className="text-slate-500">Last update</dt>
        <dd className="text-xs">
          {update ? (
            <>
              <span className={update.status === "failed" ? "text-rose-700" : ""}>{update.status}</span>
              {update.phase ? ` · ${update.phase}` : ""}{update.progress != null && running ? ` · ${Math.round(update.progress * 100)}%` : ""}
              {update.deployment_status ? ` · ${update.deployment_status}` : ""} · gallery <span className="font-mono">{update.gallery_version || "—"}</span>
              {update.installed ? " · the cabinet runs it" : update.status === "completed" ? " · waiting for the cabinet to switch" : ""}
              {update.error ? <span className="text-rose-700"> · {update.error}</span> : null}
              {` · ${(update.product_uuids || []).length} products · ${ago(update.updated_at || update.created_at)}`}
            </>
          ) : "none yet"}
        </dd>
        {next && <><dt className="text-slate-500">Waiting behind it</dt><dd className="text-xs">{next.status} · {(next.product_uuids || []).length} products</dd></>}
      </dl>
      {err && <p className="mt-2 text-xs text-rose-700">{err}</p>}
    </div>
  );
}

export function LayoutTab({ deviceId }) {
  const api = useApi();
  const names = useProductNames(api);
  const [planogram, setPlanogram] = useState(undefined);
  const [shelves, setShelves] = useState(null);
  const [training, setTraining] = useState(null);
  const [su, setSu] = useState(null);

  const loadUpdate = () => api.getDeviceShelfUpdate(deviceId).then(setSu).catch(() => setSu(null));
  useEffect(() => {
    api.getDevicePlanogram(deviceId).then(setPlanogram)
      .catch((e) => setPlanogram(e?.status === 404 ? null : { error: String(e.message || e) }));
    api.getDeviceShelves(deviceId).then(setShelves).catch(() => setShelves(null));
    api.getDeviceTrainingStatus(deviceId).then(setTraining).catch(() => setTraining(null));
    void loadUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);
  // Follow a running update.
  useEffect(() => {
    const st = su?.update?.status;
    if (!st || ["completed", "failed"].includes(st) && !su?.next) return undefined;
    const t = setInterval(loadUpdate, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [su]);

  if (planogram === undefined) return <p className="text-sm text-slate-500">Loading the layout…</p>;
  if (planogram === null) return <p className="text-sm text-slate-500">This cabinet has no layout yet. It is made in the capture app (Layout).</p>;
  if (planogram.error) return <p className="text-sm text-rose-700">The layout could not be read: {planogram.error}</p>;

  const status = {};
  for (const p of training?.products || []) status[p.product_uuid] = p.status;
  const stock = {};
  for (const sh of shelves?.shelves || []) for (const s of sh.slots || []) stock[`${sh.shelf}:${s.product_uuid}`] = s;
  const warnings = planogram.warnings || [];
  const untrained = (planogram.product_uuids || []).filter((u) => (status[u] || "untrained") === "untrained");

  return (
    <div className="space-y-4">
      <UpdateShelves deviceId={deviceId} planogram={planogram} untrained={untrained} update={su?.update} next={su?.next} onStarted={loadUpdate} />

      {(warnings.length > 0 || untrained.length > 0) && (
        <div className="card border-amber-300 bg-amber-50">
          <h2 className="mb-1 text-sm font-semibold text-amber-900">On the layout but a problem</h2>
          <ul className="list-disc pl-5 text-sm text-amber-900">
            {untrained.map((u) => <li key={u}>{nameOf(names, u)} is not trained on this cabinet's type: the cabinet cannot recognise it. Train it in the app, or take it off the layout.</li>)}
            {warnings.map((w, i) => <li key={i}>shelf {w.shelf}: {w.message || w.code}</li>)}
          </ul>
        </div>
      )}

      <div className="card overflow-x-auto">
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold">Shelves</h2>
          <span className="text-xs text-slate-500">
            layout changed {ago(planogram.updated_at)} · stock counted {shelves?.counted_at ? ago(shelves.counted_at) : "never"}{shelves?.stale ? " (the count is stale)" : ""} · edited in the capture app
          </span>
        </div>
        <div className="space-y-3">
          {(planogram.shelves || []).map((slots, i) => (
            <div key={i} className="flex gap-3">
              <div className="w-16 shrink-0 pt-2 text-xs text-slate-500">shelf {i + 1}{i === 0 ? <div className="text-[10px]">by the door</div> : null}</div>
              {slots.length === 0 ? <div className="flex-1 rounded border border-dashed border-slate-200 p-2 text-xs text-slate-400">empty</div> : (
                <div className="grid flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  {slots.map((slot) => {
                    const st = status[slot.product_uuid] || "untrained";
                    const k = stock[`${i + 1}:${slot.product_uuid}`] || {};
                    return (
                      <div key={slot.product_uuid} className="flex gap-2 rounded border border-slate-200 p-2">
                        {k.image_url ? <img src={k.image_url} alt="" className="h-14 w-14 shrink-0 rounded object-contain" /> : <div className="h-14 w-14 shrink-0 rounded bg-slate-100" />}
                        <div className="min-w-0 text-xs">
                          <div className="truncate font-medium" title={nameOf(names, slot.product_uuid)}>{k.product_name || nameOf(names, slot.product_uuid)}</div>
                          <div className="font-mono text-slate-500">{slot.ean_code || k.ean_code || "no EAN"}</div>
                          <span className={`mt-1 inline-block rounded px-1.5 py-0.5 ${STATUS_CHIP[st] || ""}`}>{st}</span>
                          <div className="mt-1 text-slate-600">
                            {k.counted_ever ? `counted ${k.counted} · sold ${k.sold_since_count} since · expect ${k.expected}` : "never counted"}
                            {k.low_stock ? <span className="text-rose-700"> · low</span> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
